import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import type { ConversationThread } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ConversationActionToolbar } from "../components/conversation-action-toolbar";
import { ConversationCardList } from "../components/conversation-card-list";
import { ConversationDetailsPanel } from "../components/conversation-details-panel";
import { ConversationHeader } from "../components/conversation-header";
import { DraftConversationModal } from "../components/draft-conversation-modal";
import { MergedTicketDivider } from "../components/merged-ticket-divider";
import { PanelResizer } from "../components/panel-resizer";
import { AttentiveThreadView } from "../components/thread-views/attentive-thread-view";
import { ContactFormThreadView } from "../components/thread-views/contact-form-thread-view";
import { EmailThreadView } from "../components/thread-views/email-thread-view";
import { FacebookThreadView } from "../components/thread-views/facebook-thread-view";
import { InstagramThreadView } from "../components/thread-views/instagram-thread-view";
import { PostscriptThreadView } from "../components/thread-views/postscript-thread-view";
import { RedoChatThreadView } from "../components/thread-views/redo-chat-thread-view";
import { SmsThreadView } from "../components/thread-views/sms-thread-view";
import { TiktokThreadView } from "../components/thread-views/tiktok-thread-view";
import { useConversationView } from "../hooks/use-conversation-view";
import { useConversationViewLayout } from "../hooks/use-conversation-view-layout";
import { useMarkConversationAsRead } from "../hooks/use-mark-conversation-as-read";
import { usePresenceViewing } from "../hooks/use-notify-presence";
import { ConversationDraftsProvider } from "../providers/conversation-drafts-context";
import { useConversationsTableContext } from "../table/conversations-table-context";
import * as styles from "./conversation-view.module.css";

const ConversationCardListPanel = memo(function ConversationCardListPanel({
  onCollapse,
}: {
  onCollapse: () => void;
}) {
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    selectedConversationId,
    openConversation,
  } = useConversationsTableContext();

  const handleCardClick = useHandler(
    (conversation: ConversationListItemResponse) => {
      openConversation(conversation.id);
    },
  );

  const handleLoadMore = useHandler(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  });

  const isCardSelected = useCallback(
    (conversation: ConversationListItemResponse) => {
      return conversation.id === selectedConversationId;
    },
    [selectedConversationId],
  );

  return (
    <ConversationCardList
      conversations={data}
      hasMore={hasNextPage}
      isCardSelected={isCardSelected}
      isFetchingMore={isFetchingNextPage}
      isLoading={isLoading}
      onCardClick={handleCardClick}
      onCollapse={onCollapse}
      onLoadMore={handleLoadMore}
    />
  );
});

interface ConversationViewProps {
  conversationId: ConversationId;
}

/**
 * Conversation view component that displays conversation data.
 * Renders threads using channel-appropriate components.
 */
export const ConversationView = memo(function ConversationView({
  conversationId,
}: ConversationViewProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const team = useRequiredContext(TeamContext);

  const { conversation, threads, isLoading, customerName, latestMessageTime } =
    useConversationView(conversationId);

  const {
    leftPanelOpen,
    toggleLeftPanel,
    rightPanelOpen,
    toggleRightPanel,
    leftPanelWidth,
    rightPanelWidth,
    handleLeftResize,
    handleRightResize,
    handleLeftResizeEnd,
    handleRightResizeEnd,
  } = useConversationViewLayout();

  useMarkConversationAsRead(conversationId);
  usePresenceViewing(conversationId);

  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setShowSpinner(false);
      return;
    }
    const timeout = setTimeout(() => setShowSpinner(true), 1000);
    return () => clearTimeout(timeout);
  }, [isLoading]);

  const handleBack = useCallback(
    () => navigate(`/stores/${team._id}/support-v2${location.search}`),
    [navigate, team._id, location.search],
  );

  if (conversation?.status === "DRAFT") {
    const draftThread = threads[0];
    if (!draftThread) {
      return null;
    }
    return (
      <DraftConversationModal
        conversation={conversation}
        onClose={handleBack}
        onCreated={(id) =>
          navigate(`/stores/${team._id}/support-v2/${id}${location.search}`)
        }
        thread={draftThread}
      />
    );
  }

  const showContent = !isLoading && conversation;

  return (
    <Flex dir="column" gap="none" height="full" overflow="hidden" w="full">
      <Flex flex={1} gap="none" style={{ minHeight: 0 }}>
        {leftPanelOpen && (
          <>
            <Flex
              borderColor="secondary"
              borderRightWidth="1px"
              borderStyle="solid"
              dir="column"
              style={{ flex: `0 0 ${leftPanelWidth}px` }}
            >
              <ConversationCardListPanel onCollapse={toggleLeftPanel} />
            </Flex>
            <PanelResizer
              onResize={handleLeftResize}
              onResizeEnd={handleLeftResizeEnd}
            />
          </>
        )}

        {showContent ? (
          <ConversationDraftsProvider
            conversationId={conversationId}
            hasDraft={conversation.numDrafts > 0}
            key={conversationId}
          >
            <Flex
              className={styles.fadeIn}
              dir="column"
              style={{ flex: 1, minWidth: 0 }}
            >
              <ConversationHeader
                actionToolbar={
                  <ConversationActionToolbar
                    conversation={conversation}
                    conversationId={conversationId}
                  />
                }
                customerName={customerName}
                latestMessageTime={latestMessageTime}
                leftPanelOpen={leftPanelOpen}
                onBack={handleBack}
                onToggleLeftPanel={toggleLeftPanel}
                onToggleRightPanel={toggleRightPanel}
                rightPanelOpen={rightPanelOpen}
                ticketNumber={conversation.ticketNumber}
              />
              <Flex
                bgColor="secondary"
                dir="column"
                grow={1}
                overflowY="auto"
                p="xl"
                style={{ minHeight: 0 }}
              >
                <Flex dir="column" gap="xl" grow={1}>
                  {threads.length === 0 ? (
                    <Flex align="center" grow={1} justify="center">
                      <Text fontSize="md" textColor="secondary">
                        No messages in this thread
                      </Text>
                    </Flex>
                  ) : (
                    threads.flatMap((thread, index) => [
                      ...(index > 0
                        ? [<MergedTicketDivider key={`divider-${thread.id}`} />]
                        : []),
                      <ThreadRenderer key={thread.id} thread={thread} />,
                    ])
                  )}
                </Flex>
              </Flex>
            </Flex>

            {rightPanelOpen && (
              <>
                <PanelResizer
                  onResize={handleRightResize}
                  onResizeEnd={handleRightResizeEnd}
                />
                <Flex
                  bgColor="primary"
                  borderColor="secondary"
                  borderLeftWidth="1px"
                  borderStyle="solid"
                  dir="column"
                  overflow="hidden"
                  style={{ flex: `0 0 ${rightPanelWidth}px`, minHeight: 0 }}
                >
                  <ConversationDetailsPanel
                    conversation={conversation}
                    onCollapse={toggleRightPanel}
                  />
                </Flex>
              </>
            )}
          </ConversationDraftsProvider>
        ) : (
          <Flex align="center" grow={1} justify="center">
            {showSpinner && <RedoSpinner />}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
});

interface ThreadRendererProps {
  thread: ConversationThread;
}

/**
 * Renders a thread using the appropriate component based on integration type.
 * This switch is exhaustive - TypeScript will error if a new integration type
 * is added without handling it here.
 */
const ThreadRenderer = memo(function ThreadRenderer({
  thread,
}: ThreadRendererProps) {
  switch (thread.integrationType) {
    case "gmail":
    case "outlook":
      return <EmailThreadView thread={thread} />;
    case "facebook":
      return <FacebookThreadView thread={thread} />;
    case "instagram":
      return <InstagramThreadView thread={thread} />;
    case "tiktok":
      return <TiktokThreadView thread={thread} />;
    case "attentive":
      return <AttentiveThreadView thread={thread} />;
    case "telnyx_sms":
      return <SmsThreadView thread={thread} />;
    case "postscript":
      return <PostscriptThreadView thread={thread} />;
    case "redoChatAi":
    case "redoChatFlow":
    case "redoChatLive":
      return <RedoChatThreadView thread={thread} />;
    case "contactForm":
      return <ContactFormThreadView thread={thread} />;
    default: {
      assertNever(thread);
    }
  }
});
